import { call, put, all, select } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import {
  apiInvestmentObjectivePortfolioProposalsPost,
  apiInvestmentObjectivePortfolioProposalsMultiplePatch,
} from '../api/investmentObjectives';
import { investmentObjectivePortfolioProposalsGet } from './investmentObjectives';
import { withId, faPaths } from '../../paths';
import { createQadraSaga } from './shared';
import {
  apiPortfolioProposalsPatch,
  apiPortfolioProposalsHoldingsPatch,
  apiPortfolioProposalsPerformanceGet,
  apiPortfolioProposalsHoldingsGet,
  apiPortfolioProposalsDetailsGet,
  apiPortfolioProposalsKpisGet,
  apiPortfolioProposalMaxDrawdownGet,
} from '../api/portfolioProposal';
import {
  PORTFOLIO_PROPOSALS_COPY,
  PORTFOLIO_PROPOSALS_PUBLISH,
  PORTFOLIO_PROPOSALS_MULTIPLE_PUBLISH,
  PORTFOLIO_PROPOSALS_HOLDINGS_PATCH,
  PORTFOLIO_PROPOSALS_HOLDINGS_GET,
  PORTFOLIO_PROPOSALS_BENCHMARK_GET,
  PORTFOLIO_PROPOSALS_DETAILS_GET,
  PORTFOLIO_PROPOSALS_KPIS_GET,
  PORTFOLIO_PROPOSALS_MAX_DRAWDOWN_GET,
  PORTFOLIO_PROPOSALS_PATCH,
} from '../actions/types';
import { getServerErrorMessage } from '../../utils';
import { actionBadRequestErrorSet, actionBadRequestErrorClear } from '../actions';
import { accountsGet } from './account';

function* portfolioProposalsCopy({
  payload: { accountId, investmentObjectiveId, name, parentProposalId, parentModelPortfolioId, onbData, onSuccess },
}) {
  const { portfolioProposal } = yield call(
    apiInvestmentObjectivePortfolioProposalsPost,
    investmentObjectiveId,
    {
      type: 'manual',
      name,
      parentProposalId,
      parentModelPortfolioId,
    }
  );

  yield investmentObjectivePortfolioProposalsGet({ payload: { investmentObjectiveId } });

  if(onSuccess) {
    onSuccess();
  }

  const baseUrl = withId(faPaths.accounts.portfolioProposals.details, [
    [accountId, 'accountId'],
    [portfolioProposal.id, 'proposalId'],
  ]);

  yield put(push(`${baseUrl}/${onbData}`));
}

const watchPortfolioProposalsCopy = createQadraSaga(portfolioProposalsCopy, {
  action: PORTFOLIO_PROPOSALS_COPY._REQUEST,
});

function* portfolioProposalsPatch({
  payload: { userId, investmentObjectiveId, proposalId, proposal, url },
}) {
  const loggedUserId = yield select((s) => s.app.user.id);

  yield call(apiPortfolioProposalsPatch, proposalId, proposal);
  yield investmentObjectivePortfolioProposalsGet({ payload: { investmentObjectiveId } });
  yield accountsGet({ payload: { userId: userId ?? loggedUserId } });

  if (url) yield put(push(url));
}

const watchPortfolioProposalsPatch = createQadraSaga(portfolioProposalsPatch, {
  action: PORTFOLIO_PROPOSALS_PATCH._REQUEST,
});

function* portfolioProposalsPublish({ payload: { investmentObjectiveId, proposalId, published, onSuccess } }) {
  yield call(apiPortfolioProposalsPatch, proposalId, { published });
  yield investmentObjectivePortfolioProposalsGet({ payload: { investmentObjectiveId } });

  if(onSuccess) {
    onSuccess();
  }
}

const watchPortfolioProposalsPublish = createQadraSaga(portfolioProposalsPublish, {
  action: PORTFOLIO_PROPOSALS_PUBLISH._REQUEST,
});

function* portfolioProposalsMultiplePublish({ payload: { portfolioProposals } }) {
  const investments = portfolioProposals.reduce((acc, p) => {
    if (acc[p.investmentObjectiveId]) {
      acc[p.investmentObjectiveId] = [
        ...acc[p.investmentObjectiveId],
        { id: p.id, published: true },
      ];
    } else {
      acc[p.investmentObjectiveId] = [{ id: p.id, published: true }];
    }
    return acc;
  }, {});

  const patches = [];
  const gets = [];
  Object.entries(investments).forEach(([investmentObjectiveId, values]) => {
    patches.push(
      call(apiInvestmentObjectivePortfolioProposalsMultiplePatch, investmentObjectiveId, {
        portfolioProposals: values,
      })
    );
    gets.push(investmentObjectivePortfolioProposalsGet({ payload: { investmentObjectiveId } }));
  });

  yield all(patches);
  yield all(gets);
}

const watchPortfolioProposalsMultiplePublish = createQadraSaga(portfolioProposalsMultiplePublish, {
  action: PORTFOLIO_PROPOSALS_MULTIPLE_PUBLISH._REQUEST,
});

function* portfolioProposalsBenchmarkGet({
  payload: { proposalId, benchmarkId, yearsTag, compareIds, portfolioId },
}) {
  const { chart } = yield call(apiPortfolioProposalsPerformanceGet, proposalId, {
    benchmarkId,
    yearsTag,
    compareIds,
    portfolioId,
  });

  yield put({
    type: PORTFOLIO_PROPOSALS_BENCHMARK_GET._SUCCESS,
    payload: {
      proposalId,
      performanceChartBenchmarkId: benchmarkId,
      performanceChart: chart,
      compareIds,
      performanceChartYearsTag: yearsTag,
    },
  });
}

const watchPortfolioProposalsBenchmarkGet = createQadraSaga(portfolioProposalsBenchmarkGet, {
  action: PORTFOLIO_PROPOSALS_BENCHMARK_GET._REQUEST,
});

export function* portfolioProposalsKpisGet({
  payload: { proposalId, proposalIds, startDate, endDate },
}) {
  if (proposalIds) {
    const calls = proposalIds.map((id) =>
      call(apiPortfolioProposalsKpisGet, id, startDate, endDate)
    );
    const res = yield all(calls);
    const payload = proposalIds.map((id, i) => ({ id, kpis: res[i].kpis }));
    yield put({ type: PORTFOLIO_PROPOSALS_KPIS_GET._SUCCESS, payload });
  } else {
    const { kpis } = yield call(apiPortfolioProposalsKpisGet, proposalId, startDate, endDate);
    yield put({
      type: PORTFOLIO_PROPOSALS_KPIS_GET._SUCCESS,
      payload: [{ id: proposalId, kpis }],
    });
  }
}

const watchPortfolioProposalsKpisGet = createQadraSaga(portfolioProposalsKpisGet, {
  action: PORTFOLIO_PROPOSALS_KPIS_GET._REQUEST,
});

export function* portfolioProposalsHoldingsGet({ payload: { proposalId } }) {
  const { proposalHoldings, riskProfile } = yield call(apiPortfolioProposalsHoldingsGet, proposalId);

  yield put({
    type: PORTFOLIO_PROPOSALS_HOLDINGS_GET._SUCCESS,
    payload: { proposalId, proposalHoldings, riskProfile },
  });
}

const watchPortfolioProposalsHoldingsGet = createQadraSaga(portfolioProposalsHoldingsGet, {
  action: PORTFOLIO_PROPOSALS_HOLDINGS_GET._REQUEST,
});

export function* portfolioProposalsDetailsGet({ payload: { proposalId } }) {
  const { portfolioProposal } = yield call(apiPortfolioProposalsDetailsGet, proposalId);

  yield put({
    type: PORTFOLIO_PROPOSALS_DETAILS_GET._SUCCESS,
    payload: { proposalId, portfolioProposal },
  });
}

const watchPortfolioProposalsDetailsGet = createQadraSaga(portfolioProposalsDetailsGet, {
  action: PORTFOLIO_PROPOSALS_DETAILS_GET._REQUEST,
});

function* portfolioProposalsHoldingsPatch({
  payload: { investmentObjectiveId, proposalId, holdings },
}) {
  yield call(apiPortfolioProposalsHoldingsPatch, proposalId, holdings);

  const calls = [
    call(investmentObjectivePortfolioProposalsGet, { payload: { investmentObjectiveId } }),
    call(portfolioProposalsKpisGet, { payload: { proposalId } }),
    call(portfolioProposalsHoldingsGet, { payload: { proposalId } }),
    call(portfolioProposalsDetailsGet, { payload: { proposalId } }),
  ];
  yield all(calls);
}

const watchPortfolioProposalsHoldingsPatch = createQadraSaga(portfolioProposalsHoldingsPatch, {
  action: PORTFOLIO_PROPOSALS_HOLDINGS_PATCH._REQUEST,
  *onBadRequestError(e) {
    const message = getServerErrorMessage(e);
    yield put(actionBadRequestErrorSet(message, PORTFOLIO_PROPOSALS_HOLDINGS_PATCH._ERROR));
  },
  *onBegin() {
    yield put(actionBadRequestErrorClear(PORTFOLIO_PROPOSALS_HOLDINGS_PATCH._ERROR));
  },
});

export function* portfolioProposalsMaxDrawdownGet({
  payload: { proposalId, startDate, endDate, benchmarkId, samplingInterval },
}) {
  const { maxDrawdown } = yield call(apiPortfolioProposalMaxDrawdownGet, {
    proposalId,
    startDate,
    endDate,
    benchmarkId,
    samplingInterval,
  });

  yield put({
    type: PORTFOLIO_PROPOSALS_MAX_DRAWDOWN_GET._SUCCESS,
    payload: { maxDrawdown, proposalId },
  });
}

const watchPortfolioProposalsMaxDrawdownGet = createQadraSaga(portfolioProposalsMaxDrawdownGet, {
  action: PORTFOLIO_PROPOSALS_MAX_DRAWDOWN_GET._REQUEST,
  *onBadRequestError(e) {
    const message = getServerErrorMessage(e);
    yield put(actionBadRequestErrorSet(message, PORTFOLIO_PROPOSALS_MAX_DRAWDOWN_GET._ERROR));
  },
  *onBegin() {
    yield put(actionBadRequestErrorClear(PORTFOLIO_PROPOSALS_MAX_DRAWDOWN_GET._ERROR));
  },
});

export default [
  watchPortfolioProposalsPatch,
  watchPortfolioProposalsCopy,
  watchPortfolioProposalsPublish,
  watchPortfolioProposalsMultiplePublish,
  watchPortfolioProposalsBenchmarkGet,
  watchPortfolioProposalsKpisGet,
  watchPortfolioProposalsHoldingsGet,
  watchPortfolioProposalsDetailsGet,
  watchPortfolioProposalsHoldingsPatch,
  watchPortfolioProposalsMaxDrawdownGet,
];
